import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  ArrowLeftIcon, 
  ArrowTopRightOnSquareIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  DocumentTextIcon,
  ShareIcon,
  LinkIcon,
  ChatBubbleBottomCenterTextIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { useState as useReactState } from 'react';
import { copyToClipboard, shareOnWhatsApp } from '../utils/shareUtils';
import { getFileIcon, getFileTypeText } from '../utils/fileIcons.jsx';

// Helper function to normalize different date formats to a Date object
const normalizeDate = (date) => {
  if (!date) return null;
  // If it's a Firestore Timestamp
  if (date.toDate) return date.toDate();
  // If it's an ISO date string
  if (typeof date === 'string') return new Date(date);
  // If it's already a Date object
  if (date instanceof Date) return date;
  return null;
};

export default function DocumentsPage() {
  const { currentAgent } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [shareMenuOpen, setShareMenuOpen] = useState(null);
  
  const documentTypes = [
    { id: 'all', name: 'All Types' },
    { id: 'pdf', name: 'PDFs' },
    { id: 'video', name: 'Videos' },
    { id: 'image', name: 'Images' },
    { id: 'document', name: 'Documents' }
  ];

  // Get file icon component based on mimeType
  const renderFileIcon = useCallback((mimeType) => {
    const { Component, className, size } = getFileIcon(mimeType, 'h-5 w-5');
    return <Component className={className} size={size} />;
  }, []);

  // Get file type text based on mimeType
  const getFileType = useCallback((mimeType) => {
    return getFileTypeText(mimeType);
  }, []);

  // Handle copy URL action
  const handleCopyUrl = async (url, docId) => {
    const success = await copyToClipboard(url);
    if (success) {
      setCopiedUrl(docId);
      setTimeout(() => setCopiedUrl(null), 2000);
    }
  };

  // Close all share menus
  const closeAllMenus = () => {
    setShareMenuOpen(null);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareMenuOpen && !event.target.closest('.share-menu-container')) {
        closeAllMenus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [shareMenuOpen]);

  // Filter and search documents
  useEffect(() => {
    let result = [...documents];
    
    // Apply type filter
    if (activeFilter !== 'all') {
      result = result.filter(doc => {
        if (activeFilter === 'pdf') return doc.mimeType === 'application/pdf';
        if (activeFilter === 'video') return doc.mimeType?.startsWith('video/');
        if (activeFilter === 'image') return doc.mimeType?.startsWith('image/');
        if (activeFilter === 'document') {
          return doc.mimeType?.includes('word') || 
                 doc.mimeType?.includes('excel') || 
                 doc.mimeType?.includes('powerpoint');
        }
        return true;
      });
    }
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(doc => 
        doc.title.toLowerCase().includes(term) ||
        doc.description?.toLowerCase().includes(term)
      );
    }
    
    setFilteredDocs(result);
  }, [documents, activeFilter, searchTerm]);

  // Format date to a more readable format with day of week
  const formatDate = useCallback((date, includeTime = false) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    
    if (isNaN(d.getTime())) return 'Invalid date';
    
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    
    if (includeTime) {
      options.weekday = 'long';
      options.hour = '2-digit';
      options.minute = '2-digit';
      options.hour12 = true;
    }
    
    return d.toLocaleDateString('en-US', options);
  }, []);

  useEffect(() => {
    const loadDocuments = async () => {
      if (!currentAgent?.id) return;
      
      try {
        setLoading(true);
        const now = new Date();
        const resourcesRef = collection(db, 'x_agents_resources');
        
        // First get all active documents
        const q = query(
          resourcesRef,
          where('active', '==', true)
        );
        
        const querySnapshot = await getDocs(q);
        const docs = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const expiryDate = normalizeDate(data.expiry_date);
          
          // Skip if expiry date is invalid or in the past
          if (!expiryDate || expiryDate < now) return;
          
          docs.push({
            id: doc.id,
            title: data.title,
            url: data.url,
            mimeType: data.mimeType,
            expiry_date: expiryDate, // Store as normalized Date object
            description: data.description || '',
            created_at: data.created_at || Timestamp.now(),
            sendable: data.sendable || false,
            sending_text: data.sending_text || ''
          });
        });
        
        // Sort documents alphabetically by title (case-insensitive)
        docs.sort((a, b) => {
          const titleA = a.title?.toLowerCase() || '';
          const titleB = b.title?.toLowerCase() || '';
          return titleA.localeCompare(titleB);
        });
        
        setDocuments(docs);
        setFilteredDocs(docs);
        setError(null);
      } catch (err) {
        console.error('Error loading documents:', err);
        setError('Failed to load documents. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadDocuments();
  }, [currentAgent?.id, formatDate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 pt-2 pb-1">
        <div className="flex items-center px-1 sm:px-0">
          <button
            onClick={() => navigate(-1)}
            className="mr-3 p-2 sm:p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200"
            aria-label="Go back"
          >
            <ArrowLeftIcon className="h-6 w-6 sm:h-5 sm:w-5 text-gray-600" />
          </button>
          <h1 className="text-lg sm:text-xl font-semibold text-gray-900">All Documents</h1>
        </div>
        
        {/* Filter and Search Bar */}
        <div className="bg-white p-2 sm:p-3 rounded-lg shadow sticky top-0 z-10 mt-2 mb-3">
          <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 text-base sm:text-sm border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {/* Document Type Filter */}
            <div className="flex items-start space-x-1 overflow-x-auto pb-1 sm:pb-0 -mx-1 px-1">
              <FunnelIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1.5 sm:mt-0" />
              <div className="flex flex-wrap gap-1.5">
                {documentTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setActiveFilter(type.id)}
                    className={`px-3 py-1.5 sm:py-1 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
                      activeFilter === type.id 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'text-gray-600 hover:bg-gray-100 active:bg-gray-200'
                    }`}
                  >
                    {type.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Results Count */}
        <div className="mb-3 text-sm text-gray-600">
          Showing {filteredDocs.length} {filteredDocs.length === 1 ? 'document' : 'documents'}
          {activeFilter !== 'all' && ` in ${documentTypes.find(t => t.id === activeFilter)?.name}`}
          {searchTerm && ` matching "${searchTerm}"`}
        </div>
        {error ? (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        ) : filteredDocs.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {documents.length === 0 
                ? 'No documents available' 
                : 'No documents match your search'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {documents.length === 0
                ? 'There are currently no documents to display.'
                : searchTerm
                  ? 'Try adjusting your search to find what you\'re looking for.'
                  : 'Try adjusting your filters to see more results.'}
            </p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {filteredDocs.map((doc) => (
                <li key={doc.id} className="active:bg-gray-50">
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block hover:bg-gray-50 active:bg-gray-100 px-4 py-4 sm:px-6 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 min-w-0 flex-1">
                        <div className="flex-shrink-0 mt-0.5">
                          {renderFileIcon(doc.mimeType)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-base sm:text-sm font-medium text-gray-900 truncate pr-2">
                              {doc.title}
                            </p>
                            <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {getFileType(doc.mimeType)}
                            </span>
                          </div>
                          <div className="mt-1">
                            <span className="text-xs sm:text-sm text-gray-500">
                              Expires: {formatDate(doc.expiry_date, false)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="ml-2 flex-shrink-0 flex items-center space-x-1">
                        <div className="relative inline-block text-left share-menu-container">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShareMenuOpen(shareMenuOpen === doc.id ? null : doc.id);
                            }}
                            className="p-1.5 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            aria-expanded="true"
                            aria-haspopup="true"
                          >
                            <ShareIcon className="h-5 w-5" />
                          </button>

                          {shareMenuOpen === doc.id && doc.sendable === true && (
                            <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                              <div className="py-1">
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    shareOnWhatsApp(doc.url, doc, {
                                      sendable: doc.sendable,
                                      sending_text: doc.sending_text
                                    });
                                    closeAllMenus();
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  <ChatBubbleBottomCenterTextIcon className="mr-3 h-5 w-5 text-gray-400" />
                                  Share via WhatsApp
                                </button>
                                <button
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    await handleCopyUrl(doc.url, doc.id);
                                    closeAllMenus();
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  {copiedUrl === doc.id ? (
                                    <>
                                      <CheckIcon className="mr-3 h-5 w-5 text-green-500" />
                                      Copied!
                                    </>
                                  ) : (
                                    <>
                                      <LinkIcon className="mr-3 h-5 w-5 text-gray-400" />
                                      Copy link
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-1.5 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                        >
                          <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                        </a>
                      </div>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
